import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUpdateWorkshopMutation, useGetWorkshopBySlugQuery } from 'api/workshopApi';
import { useGetMemberBySlugQuery, useGetAllMembersQuery } from 'api/consultancyApi';
import Layout from 'layout/Layout';
import { mediaUrl } from 'utils/mediaUrl';
import './Workshop.css';

const WorkshopUpdate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: workshopData, isLoading, isError } = useGetWorkshopBySlugQuery(slug);
  const [updateWorkshop] = useUpdateWorkshopMutation();
  const { data: allHosts } = useGetAllMembersQuery();

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    date: '',
    start_time: '',
    end_time: '',
    course_fee_original: '',
    course_fee_discounted: '',
    location: '',
    cover_photo: null,
    tagline: '',
    duration: '',
    languages: [],
    existingCoverPhoto: null,
    host_slug: null,
    post_order: '',
  });

  const [hostSlug, setHostSlug] = useState(null);
  const { data: hostData } = useGetMemberBySlugQuery(hostSlug, { skip: !hostSlug });

  useEffect(() => {
    if (workshopData?.data) {
      const { is_hosted, ...rest } = workshopData.data;
      setFormData({
        ...rest,
        languages: rest.languages || [],
        cover_photo: null,
        existingCoverPhoto: rest.cover_photo || null,
        host_slug: rest.host_slug || null,
        post_order: rest.post_order || '',
      });
      setHostSlug(rest.host_slug || null);
    }
  }, [workshopData]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'languages') {
      setFormData({ ...formData, languages: value.split(',').map(lang => lang.trim()) });
    } else if (name === 'host_slug') {
      setFormData({ ...formData, host_slug: value });
      setHostSlug(value);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, cover_photo: file }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('subtitle', formData.subtitle);
      payload.append('date', formData.date);
      payload.append('start_time', formData.start_time);
      payload.append('end_time', formData.end_time);
      payload.append('course_fee_original', formData.course_fee_original);
      payload.append('course_fee_discounted', formData.course_fee_discounted);
      payload.append('location', formData.location);
      payload.append('tagline', formData.tagline);
      payload.append('duration', formData.duration);
      payload.append('languages', JSON.stringify(formData.languages));
      if (formData.host_slug) payload.append('host_slug', formData.host_slug);
      if (formData.cover_photo instanceof File) {
        payload.append('cover_photo', formData.cover_photo);
      }
      payload.append('post_order', formData.post_order);

      await updateWorkshop({ slug, formData: payload }).unwrap();
      alert('Workshop updated successfully');
      navigate('/workshop');
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update workshop.');
    }
  };

  if (isLoading) return <p>Loading workshop data...</p>;
  if (isError) return <p>Failed to load workshop data.</p>;

  return (
    <Layout title="Update Workshop" tagline="Edit your workshop post details.">
      <div className="admin-form-container">
        <form onSubmit={handleSubmit}>
          <h2 className="form-header-title">Update Workshop</h2>
          <div className="form-grid">
            {[
              ['Title', 'title'],
              ['Subtitle', 'subtitle'],
              ['Date', 'date', 'date'],
              ['Start Time', 'start_time', 'time'],
              ['End Time', 'end_time', 'time'],
              ['Original Course Fee', 'course_fee_original', 'number'],
              ['Discounted Course Fee', 'course_fee_discounted', 'number'],
              ['Location', 'location'],
              ['Tagline', 'tagline'],
              ['Duration', 'duration'],
              ['Post Order', 'post_order', 'number'],
            ].map(([label, name, type = 'text']) => (
              <div key={name} className="form-group">
                <label className="form-label">{label}</label>
                <input
                  type={type}
                  name={name}
                  value={formData[name] || ''}
                  onChange={handleChange}
                  placeholder={label}
                  className="form-input"
                />
              </div>
            ))}

            <div className="form-group">
              <label className="form-label">Languages (comma-separated)</label>
              <input
                name="languages"
                value={formData.languages.join(', ')}
                onChange={handleChange}
                placeholder="Languages"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Host</label>
              <select
                name="host_slug"
                value={formData.host_slug || ''}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select a host</option>
                {allHosts?.data?.map(host => (
                  <option key={host.slug} value={host.slug}>
                    {host.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group col-span-2">
              <label className="form-label">Cover Photo</label>
              {(formData.cover_photo || formData.existingCoverPhoto) && (
                <img
                  src={
                    formData.cover_photo
                      ? URL.createObjectURL(formData.cover_photo)
                      : mediaUrl(formData.existingCoverPhoto)
                  }
                  alt={formData.title}
                  style={{ maxHeight: '12rem', maxWidth: '100%', marginBottom: '1rem', objectFit: 'contain', borderRadius: '0.5rem', display: 'block' }}
                />
              )}
              <label htmlFor="update_cover" className="file-upload-label">
                <p>Click to replace cover photo</p>
                <input
                  type="file"
                  id="update_cover"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {hostData?.data && (
              <div className="form-group col-span-2">
                <div className="host-info-card">
                  {hostData.data.profile_photo && (
                    <img
                      src={mediaUrl(hostData.data.profile_photo)}
                      alt={hostData.data.name}
                      className="host-avatar"
                    />
                  )}
                  <div className="host-details">
                    <h4>{hostData.data.name}</h4>
                    <p style={{ fontStyle: 'italic', marginBottom: '0.5rem' }}>{hostData.data.tagline}</p>
                    <p>{hostData.data.short_description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="ws-btn-primary">
              Update Workshop
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default WorkshopUpdate;
